import { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import froalaConfig, { mobileConfig } from "./editorConfig";
import { useIsMobile } from "@/hooks/use-mobile";
import React from "react";

const FroalaEditor = React.lazy(() => import("react-froala-wysiwyg"));

// Global registry to track active editors and prevent unsafe destruction
const activeEditors = new Set<any>();
const destroyedEditors = new Set<any>();

// Global function to safely check if an editor can be destroyed
const canSafelyDestroy = (editor: any): boolean => {
  if (!editor) return false;
  if (destroyedEditors.has(editor)) return false;

  try {
    return !!(
      editor.events &&
      typeof editor.events === "object" &&
      editor.shared &&
      typeof editor.shared === "object" &&
      editor.shared._events
    );
  } catch {
    return false;
  }
};

// Global function to mark an editor as destroyed
const markAsDestroyed = (editor: any) => {
  if (editor) {
    destroyedEditors.add(editor);
    activeEditors.delete(editor);
  }
};

// Global flag to ensure we only patch once
let globalPatchApplied = false;

// Apply comprehensive global patches to prevent unsafe destruction
const applyGlobalPatches = () => {
  if (globalPatchApplied) return;

  try {
    const FroalaEditorPrototype = (FroalaEditor as any).prototype;

    // Patch componentWillUnmount
    if (FroalaEditorPrototype.componentWillUnmount) {
      const originalComponentWillUnmount =
        FroalaEditorPrototype.componentWillUnmount;

      FroalaEditorPrototype.componentWillUnmount = function () {
        try {
          const editor = this.editor;

          if (editor && canSafelyDestroy(editor)) {
            markAsDestroyed(editor);
            return originalComponentWillUnmount.call(this);
          } else {
            if (editor) {
              markAsDestroyed(editor);
            }
            // Clear reference safely without calling destroy
            this.editor = null;
            return;
          }
        } catch (error) {
          console.warn("Global patch: Error in componentWillUnmount:", error);
          // Even on error, clean up safely
          if (this.editor) {
            markAsDestroyed(this.editor);
            this.editor = null;
          }
        }
      };
    }

    // Also patch the editor's destroy method directly if it exists
    if (FroalaEditorPrototype.componentDidMount) {
      const originalComponentDidMount = FroalaEditorPrototype.componentDidMount;

      FroalaEditorPrototype.componentDidMount = function () {
        const result = originalComponentDidMount.call(this);

        // After mount, patch the editor's destroy method if it exists
        if (this.editor && typeof this.editor.destroy === "function") {
          const originalDestroy = this.editor.destroy;

          this.editor.destroy = function () {
            try {
              // Check if editor is in valid state
              if (
                this.events &&
                typeof this.events === "object" &&
                this.shared &&
                typeof this.shared === "object" &&
                this.shared._events &&
                !destroyedEditors.has(this)
              ) {
                destroyedEditors.add(this);
                activeEditors.delete(this);
                return originalDestroy.call(this);
              } else {
                destroyedEditors.add(this);
                activeEditors.delete(this);
                return;
              }
            } catch (error) {
              console.warn("Global patch: Error in editor destroy:", error);
              destroyedEditors.add(this);
              activeEditors.delete(this);
            }
          };
        }

        return result;
      };
    }

    globalPatchApplied = true;
  } catch (error) {
    console.warn("Global patches: Failed to apply patches:", error);
  }
};

// Apply patches immediately
applyGlobalPatches();
interface EditorWithDelayProps {
  content: string;
  type: string;
  language: string;
  style?: string;
  trackingIds?: string[];
  references?: string[] | null;
  onContentChange: (type: string, language: string, newContent: string) => void;
}
// https://github.com/froala/react-froala-wysiwyg/issues/410
// Currently stuck on v4.3.1 until the above is resolved
function FroalaEditorWithDelay({
  content,
  type,
  language,
  style = "",
  trackingIds = [],
  references = null,
  onContentChange,
}: EditorWithDelayProps) {
  const [shouldRender, setShouldRender] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const froalaEditorRef = useRef<any>(null);
  const isMountedRef = useRef(true);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const [editorHeight, setEditorHeight] = useState(300);
  const errorReportedRef = useRef(false);

  const isMobile = useIsMobile();
  const editorConfig = isMobile ? mobileConfig : froalaConfig;

  // Unified error reporting helper
  const reportEditorError = (err: unknown, context: string) => {
    if (errorReportedRef.current) return; // avoid flooding
    errorReportedRef.current = true;
    const errorObj = err instanceof Error ? err : new Error(String(err));
    Sentry.withScope((scope) => {
      scope.setTag("component", "FroalaEditorWithDelay");
      scope.setTag("editor_language", language);
      scope.setTag("editor_type", type);
      scope.setTag("editor_mobile", String(isMobile));
      scope.setExtra("trackingIds_count", trackingIds.length);
      scope.setExtra("references_count", references ? references.length : 0);
      scope.setExtra("editor_height", editorHeight);
      scope.setExtra("context", context);
      scope.setFingerprint([
        "froala-editor",
        context,
        type,
        language,
        isMobile ? "mobile" : "desktop",
      ]);
      Sentry.captureException(errorObj);
    });
    setHasError(true);
  };

  // Load plugins once
  useEffect(() => {
    let cancelled = false;
    async function loadPlugins() {
      try {
        await Promise.all([
          import("froala-editor/css/froala_style.min.css"),
          import("froala-editor/css/froala_editor.pkgd.min.css"),
          import("froala-editor/js/plugins.pkgd.min.js"),
          import("froala-editor/js/plugins/link.min.js"),
          import("froala-editor/js/plugins/track_changes.min.js"),
        ]);
        if (!cancelled && isMountedRef.current) setIsInitialized(true);
      } catch (e) {
        console.warn("Froala plugin load failed", e);
        if (!cancelled && isMountedRef.current)
          reportEditorError(e, "plugin-load");
      }
    }
    loadPlugins();
    return () => {
      cancelled = true;
    };
  }, []);

  // Delay render slightly (improves layout stability)
  useEffect(() => {
    isMountedRef.current = true;
    const t = setTimeout(() => {
      if (isMountedRef.current) setShouldRender(true);
    }, 80);
    return () => {
      isMountedRef.current = false;
      clearTimeout(t);
    };
  }, []);

  const calculateHeight = () => {
    if (!editorContainerRef.current) return;
    try {
      const rect = editorContainerRef.current.getBoundingClientRect();
      let viewportH = window.innerHeight;
      if (isMobile)
        viewportH = window.visualViewport?.height || window.innerHeight;

      // Measure fixed ResultOptions bar if present
      const optionsEl = document.querySelector(
        ".result-options-fixed"
      ) as HTMLElement | null;
      const optionsH = optionsEl ? optionsEl.getBoundingClientRect().height : 0;

      // Base paddings (space for internal Froala toolbars etc.)
      const mobilePadding = 24; // nav/spacing
      const desktopPadding = 90; // previous logic
      const basePad = isMobile ? mobilePadding : desktopPadding;

      // Gap so text not flush under bar
      const gap = 12;

      const available = viewportH - rect.top - basePad - optionsH - gap;
      const newH = Math.max(240, available);
      if (Math.abs(newH - editorHeight) > 6) {
        setEditorHeight(newH);
      }
    } catch (e) {
      console.warn("height calc failed", e);
      reportEditorError(e, "height-calc");
    }
  };

  // Height listeners
  useEffect(() => {
    if (!shouldRender) return;
    calculateHeight();
    const onResize = () => calculateHeight();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    if (window.visualViewport)
      window.visualViewport.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      if (window.visualViewport)
        window.visualViewport.removeEventListener("resize", onResize);
    };
  }, [shouldRender, isMobile]);

  // Recalc on content changes (layout growth)
  useEffect(() => {
    if (!shouldRender) return;
    const t = setTimeout(calculateHeight, 120);
    return () => clearTimeout(t);
  }, [content, references, shouldRender]);

  const isEmptyContent = (html: string): boolean => {
    const stripped = html?.trim() || "";
    return (
      stripped === "" ||
      stripped === "<p></p>" ||
      stripped === "<p><br></p>" ||
      stripped === "<br>" ||
      stripped === "<div></div>"
    );
  };

  if (!shouldRender || !isMountedRef.current || hasError || !isInitialized) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-64 gap-3 bg-gray-100 animate-pulse">
        <div className="text-gray-600">
          {hasError
            ? "Editor error"
            : !isInitialized
              ? "Loading editor plugins..."
              : !shouldRender
                ? "Preparing editor..."
                : "Editor shutting down..."}
        </div>
        {hasError && (
          <button
            type="button"
            onClick={() => {
              // Reset local error flags and force a remount of the editor subtree
              errorReportedRef.current = false;
              setHasError(false);
              setShouldRender(false);
              setTimeout(() => setShouldRender(true), 0);
            }}
            className="px-3 py-1 text-sm font-medium text-white rounded bg-primary-600 hover:bg-primary/90"
          >
            Reload editor
          </button>
        )}
      </div>
    );
  }

  // Safely handle track changes
  if (
    froalaEditorRef.current?.editor?.track_changes &&
    froalaEditorRef.current.editor.events &&
    isMountedRef.current &&
    !hasError
  ) {
    try {
      const pendingChanges =
        froalaEditorRef.current.editor.track_changes.getPendingChanges();
      if (pendingChanges.length === 0 && trackingIds.length > 0) {
        // If there are no pending changes, add the tracking IDs to the editor
        trackingIds.forEach((id) => {
          try {
            froalaEditorRef.current.editor.track_changes.pushChange(id);
          } catch (error) {
            console.warn("Error adding tracking ID:", error);
          }
        });
      }
    } catch (error) {
      console.warn("Error handling track changes:", error);
      reportEditorError(error, "track-changes");
    }
  }

  let isRtl = false;
  switch (language) {
    case "arabic":
    case "aramaic":
    case "azeri":
    case "dhivehi":
    case "hebrew":
    case "kurdish":
    case "farsi":
    case "urdru":
      isRtl = true;
      break;
    default:
      isRtl = false;
  }

  return (
    <div
      ref={editorContainerRef}
      className={
        isMobile
          ? "flex flex-col w-full min-h-[80dvh] h-[80dvh] overflow-hidden"
          : "flex flex-col w-full h-full overflow-hidden"
      }
      // Add bottom padding equal to ResultOptions height so content not hidden under fixed bar
      style={(() => {
        const optionsEl = document.querySelector(
          ".result-options-fixed"
        ) as HTMLElement | null;
        const pad = optionsEl
          ? optionsEl.getBoundingClientRect().height + 16
          : 0; // +gap
        return { paddingBottom: pad } as React.CSSProperties;
      })()}
    >
      <div
        className="flex flex-col flex-1"
        style={{ height: editorHeight, minHeight: editorHeight }}
      >
        {(() => {
          try {
            return (
              <React.Suspense
                fallback={
                  <div className="flex items-center justify-center w-full h-64 bg-gray-100 animate-pulse">
                    <div className="text-gray-600">Loading editor...</div>
                  </div>
                }
              >
                <FroalaEditor
                  key={`froala-${type}-${language}`}
                  ref={froalaEditorRef}
                  model={content}
                  config={{
                    ...editorConfig,
                    height: editorHeight,
                    iframe: true,
                    useClasses: true,
                    iframeStyle:
                      style +
                      ".fr-track-hide { display: none; } " +
                      `iframe { width: 100%; height: ${editorHeight}px !important; min-height: ${editorHeight}px; }  html { overflow: scroll !important } ` +
                      `body { ${isMobile ? "font-size: 8pt; padding: 0.2in;" : ""} }`,
                    direction: isRtl ? "rtl" : "ltr",
                    events: {
                      ...editorConfig.events,
                      "froalaEditor.initialized": function () {
                        if (froalaEditorRef.current?.editor) {
                          const editorInstance = froalaEditorRef.current.editor;
                          activeEditors.add(editorInstance);
                          // guard dev-only unsafe calls
                          try {
                            const ed: any = editorInstance;
                            if (
                              ed &&
                              typeof ed.destroy === "function" &&
                              !ed._safeDestroyPatched
                            ) {
                              const originalDestroy = ed.destroy.bind(ed);
                              ed.destroy = function () {
                                try {
                                  if (
                                    this &&
                                    this.events &&
                                    typeof this.events === "object" &&
                                    this.shared &&
                                    typeof this.shared === "object" &&
                                    this.shared._events &&
                                    !destroyedEditors.has(this)
                                  ) {
                                    destroyedEditors.add(this);
                                    activeEditors.delete(this);
                                    return originalDestroy();
                                  }
                                  destroyedEditors.add(this);
                                  activeEditors.delete(this);
                                  return;
                                } catch (e) {
                                  destroyedEditors.add(this);
                                  activeEditors.delete(this);
                                  return;
                                }
                              };
                              ed._safeDestroyPatched = true;
                            }
                          } catch {}
                        }
                      },
                      "froalaEditor.destroy": function () {},
                      "froalaEditor.beforeDestroy": function () {
                        const editorInstance = froalaEditorRef.current?.editor;
                        if (
                          editorInstance &&
                          canSafelyDestroy(editorInstance)
                        ) {
                          return true;
                        } else {
                          if (editorInstance) {
                            markAsDestroyed(editorInstance);
                          }
                          return false;
                        }
                      },
                      "froalaEditor.error": function (
                        _e: any,
                        _ed: any,
                        errorData: any
                      ) {
                        console.warn("Froala editor error event", errorData);
                        if (isMountedRef.current) {
                          reportEditorError(
                            errorData || new Error("froalaEditor.error event"),
                            "event-error"
                          );
                        }
                      },
                    },
                  }}
                  onModelChange={(model: string) => {
                    if (!isMountedRef.current || hasError) return;
                    try {
                      if (isEmptyContent(model)) {
                        onContentChange(type, language, "");
                      } else {
                        onContentChange(type, language, model);
                      }
                    } catch (error) {
                      console.warn("Error in onModelChange:", error);
                      if (isMountedRef.current)
                        reportEditorError(error, "model-change");
                    }
                  }}
                />
              </React.Suspense>
            );
          } catch (error) {
            console.error("Error rendering Froala editor:", error);
            if (isMountedRef.current) reportEditorError(error, "render");
            return (
              <div className="flex items-center justify-center w-full h-64 border border-red-200 bg-red-50">
                <div className="text-red-600">
                  Editor failed to load. Please refresh the page.
                </div>
              </div>
            );
          }
        })()}
      </div>
    </div>
  );
}

export default FroalaEditorWithDelay;
